import { useQuery } from '@tanstack/react-query';
import type { FieldSpec, FormValues } from '@/components/ChildRecords';
import { formatNumber, todayIso } from '@/components/live';
import { listRows } from '@/api/resources';
import { plotOptions, unitOptions } from '@/api/lookups';
import { useRows } from '@/hooks/useResource';
import { DONE_BY } from '@/lib/practiceLists';
import { isSupabaseConfigured } from '@/lib/supabase';

export type Option = { id: string; label: string };

interface SpeciesLine {
  id: string;
  plot_id: string;
  species_id: string;
  species_label: string | null;
  tree_count: number | null;
}
interface SpeciesMeasure { id: string; production_unit_id: string | null }

/**
 * What the six plot practice registers ask for once they are read ACROSS plots (owner,
 * 2026-09-25: "these all make as ui pages under Plot management").
 *
 * Inside a plot record the parent settled two of these questions for free: which plot, and which
 * of that plot's species lines. A register spanning every plot has to ask both, and the second
 * answer depends on the first - offering a farmer in Kolar the species lines of a plot in Eluru
 * is how a growth observation ends up on the wrong trees. `optionsFor` on the species field is
 * what keeps the two in step.
 */
export function usePracticePickers() {
  const plots = useQuery({ queryKey: ['picker', 'plots', ''], queryFn: () => plotOptions(), enabled: isSupabaseConfigured });
  // Every line on every plot, narrowed per form by the plot chosen. One request rather than one
  // per plot opened: the drawer has to answer the moment the plot is picked, and a query that
  // starts then shows an empty dropdown first, which reads as "this plot has no species".
  const lines = useRows<SpeciesLine>('plot_species_inventories', { orderBy: { column: 'created_at' } });
  const measures = useRows<SpeciesMeasure>('species_measures', {});
  const workers = useQuery({
    queryKey: ['picker', 'workers'], enabled: isSupabaseConfigured,
    queryFn: () => listRows<{ id: string; full_name: string; worker_code: string | null }>('workers', { orderBy: { column: 'full_name' } }),
  });
  const contractors = useQuery({
    queryKey: ['picker', 'contractors'], enabled: isSupabaseConfigured,
    queryFn: () => listRows<{ id: string; name: string; contractor_code: string | null }>('contractors', { orderBy: { column: 'name' } }),
  });
  const units = useQuery({ queryKey: ['ref', 'unit', 'quantity'], queryFn: () => unitOptions('quantity'), enabled: isSupabaseConfigured });

  const allLines = lines.data ?? [];
  const linesOf = (plotId: string): Option[] => allLines
    .filter((row) => row.plot_id === plotId)
    .map((row) => ({ id: row.id, label: `${row.species_label ?? 'Species'}${row.tree_count != null ? ` - ${row.tree_count} trees` : ''}` }));

  return {
    plots: (plots.data ?? []) as Option[],
    linesOf,
    /** True when the plot is chosen and has no species line: the message differs from "pick a plot". */
    plotHasNoSpecies: (form: FormValues) => Boolean(form.plot_id) && linesOf(String(form.plot_id)).length === 0,
    workers: (workers.data ?? []).map((row) => ({ id: row.id, label: `${row.full_name}${row.worker_code ? ` (${row.worker_code})` : ''}` })),
    contractors: (contractors.data ?? []).map((row) => ({ id: row.id, label: `${row.name}${row.contractor_code ? ` (${row.contractor_code})` : ''}` })),
    units: (units.data ?? []).map((row) => ({ id: row.id, label: row.label })),
    productionUnitOf: (lineId: string) => {
      const speciesId = allLines.find((row) => row.id === lineId)?.species_id;
      return (measures.data ?? []).find((row) => row.id === speciesId)?.production_unit_id ?? null;
    },
  };
}

/** The plot every practice record hangs off, first field on every one of these forms. */
export function plotField(plots: Option[]): FieldSpec {
  return { key: 'plot_id', label: 'Plot', type: 'select', options: plots, required: true,
    placeholder: plots.length ? 'Select the plot' : 'No plots you can see yet' };
}

/**
 * The species line, narrowed to the plot chosen. `required` decides whether the register can
 * record something about the whole plot: weeding covers a plot, a growth observation is of one
 * species and nothing else.
 */
export function speciesField(pick: ReturnType<typeof usePracticePickers>, required: boolean): FieldSpec {
  return {
    key: 'species_inventory_id',
    label: required ? 'Species' : 'Species (optional)',
    type: 'select',
    required,
    optionsFor: (form) => pick.linesOf(String(form.plot_id ?? '')),
    // Three different empty lists, three different things to say. Left to the generic "No species
    // on record yet" all three read as a register that is empty, and only one of them is.
    placeholderFor: (form) => (!form.plot_id ? 'Choose the plot first'
      : pick.plotHasNoSpecies(form) ? 'This plot has no tree inventory yet'
        : required ? 'Select species' : 'Whole plot'),
    hint: required ? 'Only the species on the plot chosen above.' : 'Leave blank when it covers the whole plot.',
  };
}

/**
 * Who did it: a farmer, a worker or contractor from the registers, or a named person. Shared by
 * all six registers, so they cannot drift into asking the same question different ways. It was
 * also shared with the plot record's own copies of these sections until the owner had them taken
 * out on 2026-09-25 ("we can remove these here"); the registers are now the only door.
 */
export function doneByFields(prefix: 'done_by' | 'applied_by', workers: Option[], contractors: Option[]): FieldSpec[] {
  const type = `${prefix}_type`;
  return [
    { key: type, label: prefix === 'done_by' ? 'Done by' : 'Applied by', type: 'select', options: DONE_BY, required: true },
    { key: `${prefix}_worker_id`, label: 'Worker', type: 'select', options: workers, required: true,
      placeholder: workers.length ? 'Select worker' : 'No workers registered yet', showIf: (form) => form[type] === 'worker' },
    { key: `${prefix}_contractor_id`, label: 'Contractor', type: 'select', options: contractors, required: true,
      placeholder: contractors.length ? 'Select contractor' : 'No contractors registered yet', showIf: (form) => form[type] === 'contractor' },
    { key: `${prefix}_name`, label: 'Name', type: 'text', required: true, showIf: (form) => form[type] === 'family_member' || form[type] === 'other' },
  ];
}

/** A date that has already happened, which every one of these observations is. */
export const dateNotFuture = (form: FormValues, key: string, label: string) =>
  form[key] && String(form[key]) > todayIso() ? `${label} cannot be in the future.` : undefined;

/**
 * A species line belongs to one plot. Change the plot on a half-filled form and the line already
 * chosen is left behind pointing at the old one, which saves a record onto trees that are not
 * there. The dropdown no longer offers it; this refuses it.
 */
export const speciesBelongsToPlot = (pick: ReturnType<typeof usePracticePickers>, form: FormValues) =>
  form.species_inventory_id && !pick.linesOf(String(form.plot_id ?? '')).some((line) => line.id === form.species_inventory_id)
    ? 'That species is not on the plot chosen. Pick one of this plot\'s species.' : undefined;

/** The two columns every one of these registers leads with: which plot, and whose. */
export const plotCell = (row: { plot_code: string | null; farmer_name: string | null }) => (
  <span className="cell-stack"><strong>{row.plot_code ?? '--'}</strong><small className="muted">{row.farmer_name ?? ''}</small></span>
);

export const area = (value: number | null) => (value == null ? '--' : `${formatNumber(value, 2)} ha`);
